"use client";

import type { ScriptSlug, UserRole } from "@/types/script";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import Link from "next/link";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { formatTimeAgo } from "@/lib/format-time-ago";
import * as z from "zod";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { useState, useRef } from "react";
import { useToast } from "@/components/ui/use-toast";
import { env } from "@/env";
import { BadgeCheck, Send, SmilePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { buildAvatarLink } from "@/lib/image";

const createCommentSchema = z.object({
  content: z
    .string()
    .min(3, "Comment must be at least 3 characters long")
    .max(1000, "Comment must be at most 1000 characters long"),
});

type CommentFormData = z.infer<typeof createCommentSchema>;

interface CommentsProps {
  script: ScriptSlug;
}

export default function Comments({ script }: CommentsProps) {
  const { toast } = useToast();
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showButtons, setShowButtons] = useState(false);
  const { user, accessToken } = useAuth();
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<CommentFormData>({
    resolver: zodResolver(createCommentSchema),
    defaultValues: {
      content: "",
    },
  });

  const onSubmit = async (data: CommentFormData) => {
    if (!user) {
      return toast({
        title: "Authentication required",
        description: "Please sign in to comment",
        variant: "destructive",
      });
    }

    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/post/${script.slug}/comments`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(data),
        },
      );

      const result = await response.json();

      if (result.message !== "OK") {
        return toast({
          title: "Error",
          description: result.message,
          variant: "destructive",
        });
      }

      toast({
        title: "Success",
        description: "Comment submitted successfully",
      });

      // Reset the form and update comments list
      setValue("content", "");
      setShowButtons(false);
      script.comments.push({
        id: result.data.comment.id,
        content: data.content,
        user: {
          username: user.username,
          avatar: user.avatar!,
          verified: user.verified,
          role: user.role,
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit comment",
        variant: "destructive",
      });
    }
  };

  const handleEmojiSelect = (emoji: { native: string }) => {
    setValue("content", `${control._formValues.content ?? ""}${emoji.native}`);
  };

  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-heading text-balance text-lg font-medium tracking-tight lg:text-xl">
        {script.comments.length} Comment
        {script.comments.length != 1 ? "s" : ""}
      </h2>

      {/* Comment form */}
      {user ? (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-2">
          <div className="flex flex-row items-start gap-2">
            <Avatar>
              <AvatarImage
                src={buildAvatarLink(user.username!)}
                alt="User avatar"
              />
              <AvatarFallback>{user.username[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Add a comment..."
                    className="flex-1"
                    rows={1}
                    onFocus={() => setShowButtons(true)}
                  />
                )}
              />
              {errors.content && (
                <p className="text-sm text-red-600">{errors.content.message}</p>
              )}

              {showButtons && (
                <div className="relative mt-2 flex flex-row items-start justify-between gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <SmilePlus className="h-5 w-5" />
                  </Button>
                  {showEmojiPicker && (
                    <div
                      ref={emojiPickerRef}
                      className="absolute z-10"
                      style={{ top: "100%", left: 0 }}
                    >
                      <Picker data={data} onEmojiSelect={handleEmojiSelect} />
                    </div>
                  )}
                  <div className="flex flex-row items-center space-x-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowButtons(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" className="gap-2 self-end" size="sm">
                      <Send size={12} />
                      Comment
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      ) : (
        <p className="text-muted-foreground">
          <Link href="/sign-in" className="text-primary underline">
            Sign in
          </Link>{" "}
          to comment
        </p>
      )}

      <ScrollArea className="mt-6 max-h-[600px] flex-1">
        <ul className="flex flex-col gap-4">
          {script.comments.map((comment: Comment) => (
            <li key={comment.id} className="mt-3 first:mt-0">
              <Comment {...comment} />
            </li>
          ))}
        </ul>
      </ScrollArea>
    </div>
  );
}

interface Comment {
  id: string;
  content: string;
  user: {
    username: string;
    avatar?: string;
    verified: boolean;
    role: UserRole;
  };
  created_at: string;
  updated_at: string;
}

function Comment({ user, content, created_at }: Comment) {
  return (
    <div className="flex flex-row items-start gap-2">
      <Avatar>
        <AvatarImage
          src={buildAvatarLink(user!.username!)}
          alt="Author avatar"
        />
        <AvatarFallback>{user?.username[0]}</AvatarFallback>
      </Avatar>
      <div className="flex flex-1 flex-col">
        <span className="flex flex-row items-center gap-1.5 text-[14px]">
          <p className="font-heading inline-flex items-center gap-0.5 break-words font-medium leading-tight">
            <Link
              href={`/user/${user.username}`}
              className="text-foreground transition hover:underline"
            >
              @{user.username}
            </Link>
            {user.role != "User" && (
              <Tooltip>
                <TooltipTrigger className="flex justify-center">
                  <BadgeCheck className="size-4 fill-primary text-card" />
                </TooltipTrigger>
                <TooltipContent>Verified author</TooltipContent>
              </Tooltip>
            )}
          </p>
          <p className="text-[12px] text-muted-foreground">
            {formatTimeAgo(created_at)}
          </p>
        </span>
        <span className="mt-1 flex flex-col break-words text-[14px]">
          <span>{content}</span>
        </span>
      </div>
    </div>
  );
}
